import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { api, type StatsTab } from '../lib/api';
import { cn } from '../lib/utils';
import { ICON, PageHeader, Sheet, Tabs } from '../components/ui';
import { LoonTab } from '../components/stats/LoonTab';
import { UrenTab } from '../components/stats/UrenTab';
import { ObjectenTab } from '../components/stats/ObjectenTab';
import { VerdienstenTab } from '../components/stats/VerdienstenTab';
import { VerlofTab } from '../components/stats/VerlofTab';
import { TrendsTab } from '../components/stats/TrendsTab';

const TABS: Array<{ value: StatsTab; label: string }> = [
  { value: 'loon', label: 'Loon' },
  { value: 'uren', label: 'Uren' },
  { value: 'objecten', label: 'Objecten' },
  { value: 'verdiensten', label: 'Per uur' },
  { value: 'verlof', label: 'Verlof' },
  { value: 'trends', label: 'Jaren vergelijken' },
];

export function StatistiekenPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [tab, setTab] = useState<StatsTab>('loon');
  const tabLabel = TABS.find((t) => t.value === tab)?.label ?? tab;

  return (
    <div className="page">
      <PageHeader
        title="Statistieken"
        sub="Loon, uren en verlof per jaar, vergeleken met het jaar ervoor."
        actions={
          <>
            <label htmlFor="stats-year" className="sr-only">Jaar</label>
            <select id="stats-year" className="input w-auto" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <ExportMenu
              items={[
                { href: api.exportView.statsTabXlsxUrl(tab, year), icon: FileSpreadsheet, label: `${tabLabel} ${year} als Excel` },
                { href: api.tax.yearUrl(year), icon: FileText, label: 'Jaaroverzicht om te printen', meta: 'Handig voor de belastingaangifte' },
                { href: api.exportView.jaaroverzichtXlsxUrl(year), icon: FileSpreadsheet, label: 'Jaaroverzicht als Excel' },
              ]}
            />
          </>
        }
      />

      <Tabs items={TABS} value={tab} onChange={setTab} />

      {tab === 'loon' && <LoonTab year={year} />}
      {tab === 'uren' && <UrenTab year={year} />}
      {tab === 'objecten' && <ObjectenTab year={year} />}
      {tab === 'verdiensten' && <VerdienstenTab year={year} />}
      {tab === 'verlof' && <VerlofTab year={year} />}
      {tab === 'trends' && <TrendsTab year={year} />}
    </div>
  );
}

/** Eén knop "Downloaden" met de exports in een klein menu. */
function ExportMenu({
  items,
}: {
  items: Array<{ href: string; icon: typeof FileText; label: string; meta?: string }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const wide = useWide();

  useEffect(() => {
    if (!open || !wide) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, wide]);

  const links = items.map((it) => (
    <a
      key={it.label}
      role="menuitem"
      href={it.href}
      target="_blank"
      rel="noopener"
      onClick={() => setOpen(false)}
      className="row-link hover:bg-slate-700/40"
    >
      <it.icon className={cn(ICON.sm, 'shrink-0 text-slate-400')} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-slate-100">{it.label}</div>
        {it.meta && <div className="truncate text-xs text-slate-400">{it.meta}</div>}
      </div>
    </a>
  ));

  return (
    <div ref={ref} className="relative">
      <button type="button" className="btn-secondary" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Download className={ICON.sm} aria-hidden />
        Downloaden
        <ChevronDown className={cn(ICON.sm, 'text-slate-400 transition-transform duration-fast ease-calm', open && 'rotate-180')} aria-hidden />
      </button>
      {open && wide && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-72 overflow-hidden rounded-xl border border-surface-line bg-surface-raised py-1 animate-enter"
        >
          {links}
        </div>
      )}
      <Sheet open={open && !wide} onClose={() => setOpen(false)} title="Downloaden">
        <div role="menu" className="list -mx-4">
          {links}
        </div>
      </Sheet>
    </div>
  );
}

/** Breder dan een telefoon (Tailwind `sm`). */
function useWide() {
  const query = '(min-width: 640px)';
  const [wide, setWide] = useState<boolean>(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}
